import { useEffect, useRef } from 'react'
import { MAIL_SEARCH_ID } from '../mailView'
import type { MailBoxApi } from './useMailBox'

const isTyping = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))

/**
 * Gmail benzeri tek tuş kısayolları. Yazı alanındayken, yazma penceresi açıkken
 * ya da Ctrl/⌘/Alt basılıyken devre dışıdır.
 */
export function useMailShortcuts(box: MailBoxApi, actions: { compose: () => void; help: () => void; disabled: boolean }) {
  const handler = useRef<(e: KeyboardEvent) => void>(() => undefined)

  useEffect(() => {
    handler.current = (e: KeyboardEvent) => {
      if (actions.disabled || box.compose.open || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      const list = box.filtered
      const m = box.selected
      const index = m ? list.findIndex((x) => x.id === m.id || x.thread_id === m.thread_id) : -1
      const run = (fn: () => void) => {
        e.preventDefault()
        fn()
      }
      switch (e.key) {
        case 'j':
        case 'ArrowDown': {
          const next = list[index + 1]
          if (next) run(() => box.selectMessage(next.id))
          break
        }
        case 'k':
        case 'ArrowUp': {
          const prev = index > 0 ? list[index - 1] : undefined
          if (prev) run(() => box.selectMessage(prev.id))
          break
        }
        case 'Escape':
          if (m) run(box.closeMessage)
          break
        case '/':
          run(() => document.getElementById(MAIL_SEARCH_ID)?.focus())
          break
        case 'c':
          run(actions.compose)
          break
        case '?':
          run(actions.help)
          break
        default:
          if (!m) return
          if (e.key === 'r') run(() => box.openCompose('reply', m))
          else if (e.key === 'a') run(() => box.openCompose('replyAll', m))
          else if (e.key === 'f') run(() => box.openCompose('forward', m))
          else if (e.key === 's') run(() => box.toggleFavorite(m.id))
          else if (e.key === 'u') run(() => box.setRead([m.id], false))
          else if ((e.key === '#' || e.key === 'Delete') && m.folder !== 'TRASH') run(() => box.moveToFolder(m.id, 'TRASH'))
      }
    }
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => handler.current(e)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

export const MAIL_SHORTCUTS: { keys: string[]; action: string }[] = [
  { keys: ['J', '↓'], action: 'next' },
  { keys: ['K', '↑'], action: 'prev' },
  { keys: ['Esc'], action: 'close' },
  { keys: ['R'], action: 'reply' },
  { keys: ['A'], action: 'replyAll' },
  { keys: ['F'], action: 'forward' },
  { keys: ['S'], action: 'star' },
  { keys: ['U'], action: 'unread' },
  { keys: ['#', 'Del'], action: 'trash' },
  { keys: ['C'], action: 'compose' },
  { keys: ['/'], action: 'search' },
  { keys: ['?'], action: 'help' },
]
